import { useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@pps/ui/components/ui/button';
import { toast } from 'sonner';
import { useWhatsAppConversations } from '@/hooks/useWhatsApp';
import { useScopedClubIds } from '@/hooks/useScopedClubIds';
import { playNotificationSound } from '@/lib/notificationSound';
import { Instagram } from 'lucide-react';
import { WhatsAppIcon } from '@/components/icons/WhatsAppIcon';
import { fotografarLeitura, mensagensNovas, type EstadoDeLeitura } from '@/lib/mensagens-novas';

function ChannelButton({
  channel,
  icon: Icon,
  route,
  iconClassName,
}: {
  channel: 'whatsapp' | 'instagram';
  icon: React.ComponentType<{ className?: string }>;
  route: string;
  iconClassName?: string;
}) {
  const navigate = useNavigate();
  const { data: conversations = [] } = useWhatsAppConversations();

  const unreads = conversations
    .filter(c => c.channel === channel)
    .reduce((sum, c) => sum + (c.unread_count || 0), 0);

  return (
    <Button
      variant="ghost"
      size="icon"
      className="relative h-9 w-9"
      onClick={() => navigate(route)}
      title={channel === 'whatsapp' ? 'WhatsApp' : 'Instagram'}
    >
      <Icon className={`h-4 w-4 ${iconClassName || ''}`} />
      {unreads > 0 && (
        <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-bold text-destructive-foreground">
          {unreads > 99 ? '99+' : unreads}
        </span>
      )}
    </Button>
  );
}

export function SocialNotifications() {
  const navigate = useNavigate();
  const { data: conversations = [], isFetched } = useWhatsAppConversations();
  const { scopedClubIds } = useScopedClubIds();
  const escopo = scopedClubIds.join('|');
  // Foto da leitura anterior; null até a 1ª carga (abrir o CRM não é "novo").
  // Trocar o escopo de clubes (ex.: A → A+B) recomeça do zero: a 1ª leitura
  // do escopo novo traz conversas de B que não são mensagem chegando agora.
  const anterior = useRef<Map<string, EstadoDeLeitura> | null>(null);
  const escopoDaFoto = useRef(escopo);

  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  useEffect(() => {
    if (escopoDaFoto.current !== escopo) {
      escopoDaFoto.current = escopo;
      anterior.current = null;
    }
    if (!isFetched) return;
    // Só mensagem recebida avisa — "Marcar como não lida" sobe a contagem sem
    // mexer em last_message_at e não pode tocar o som (ver mensagensNovas).
    const novas = mensagensNovas(anterior.current, conversations);
    anterior.current = fotografarLeitura(conversations);
    if (novas.length > 0) {
      const newest = novas[0];
      const contactLabel = newest?.contact_name || newest?.phone || 'desconhecido';
      const channelLabel = newest?.channel === 'instagram' ? 'Instagram' : 'WhatsApp';
      const route = newest?.channel === 'instagram' ? '/instagram' : '/whatsapp';

      toast(`Nova mensagem de ${contactLabel} (${channelLabel})`, {
        action: { label: 'Abrir', onClick: () => navigate(route) },
      });

      playNotificationSound();

      if ('Notification' in window && Notification.permission === 'granted') {
        try {
          const n = new Notification(`Nova mensagem ${channelLabel}`, {
            body: contactLabel,
            icon: '/favicon.png',
            tag: 'social-new-msg',
          });
          n.onclick = () => {
            window.focus();
            navigate(route);
            n.close();
          };
        } catch {
          // Silent fail
        }
      }
    }
  }, [escopo, isFetched, conversations, navigate]);

  return (
    <>
      <ChannelButton channel="whatsapp" icon={WhatsAppIcon} route="/whatsapp" />
      <ChannelButton channel="instagram" icon={Instagram} route="/instagram" />
    </>
  );
}
